import type {
  NotificationEvent,
  NotificationEventType,
  NotificationDirection,
} from '../../types/notifications.types';

/**
 * Per-event-type visual config for the notifications page.
 *
 * Each entry pairs:
 *   - `emoji`: a leading glyph that mirrors the Slack rendering
 *     convention (🤖 = automation, ✋ = manual order, etc.). Keep
 *     this in sync with `services/position-unwinder/src/notification/
 *     slack-message.builder.ts` so the in-app feed and Slack channel
 *     are visually parallel.
 *   - `color`: hex used for the row's left-border accent + the type
 *     chip's outline. Themed by category so the operator can scan
 *     the feed by color (greens = success, reds = errors, blues =
 *     placement, purples = manual, oranges/yellows = warnings).
 *   - `label` (optional): friendlier display text for the chip when
 *     the raw event-type id is too terse. Defaults to the type id.
 *
 * Keeping the config as a single typed map rather than spreading it
 * across components means a new event type only needs one entry to
 * become first-class on the page (chip + border + emoji + summary
 * row). The `Record<NotificationEventType, _>` shape is enforced
 * exhaustively at compile time.
 */
export interface NotificationDisplayConfig {
  emoji: string;
  color: string;
  label?: string;
}

export const NOTIFICATION_DISPLAY: Record<
  NotificationEventType,
  NotificationDisplayConfig
> = {
  // ─── Order lifecycle (automation, UI-only) ─────────────────────────────
  // Blues for placement / amend so the resting-order family reads as
  // "automation moving the book" at a glance.
  maker_placed: { emoji: '🤖', color: '#2196f3', label: 'maker placed' },
  maker_adjusted: { emoji: '🔧', color: '#03a9f4', label: 'amended' },
  maker_cancelled: { emoji: '🚫', color: '#607d8b', label: 'cancelled' },
  // Fill family splits into TWO chips: green ✅ `filled` for FULL fills
  // (maker / counter / crossing / recovery / manual full executions)
  // and yellow 🟡 `partial` for PARTIAL fills (including accumulated
  // partials). Anything below "full" is yellow so the operator
  // immediately sees "in motion, not done" without reading the row.
  // The expanded panel's `Fill kind` row spells out the exact subtype
  // (e.g. "Counter fill (full)", "Maker fill (partial, accumulated)")
  // for finer-grained disambiguation. Source chip (Automation / Manual)
  // and direction chip (Entry / Unwind) carry those dimensions
  // separately. Multiple types intentionally share the same chip
  // label so the Type filter dropdown groups them into ONE option —
  // see `buildFilterGroups` in NotificationsPage.tsx.
  maker_partial_fill: { emoji: '🟡', color: '#fdd835', label: 'partial' },
  partial_fill_accumulated: { emoji: '🟡', color: '#fdd835', label: 'partial' },
  maker_filled: { emoji: '✅', color: '#4caf50', label: 'filled' },
  counter_placed: { emoji: '🤖', color: '#00bcd4', label: 'counter placed' },
  counter_filled: { emoji: '✅', color: '#4caf50', label: 'filled' },
  crossing_executed: { emoji: '✅', color: '#4caf50', label: 'filled' },
  crossing_partial: { emoji: '🟡', color: '#fdd835', label: 'partial' },
  crossing_accumulated: { emoji: '🟡', color: '#fdd835', label: 'partial' },
  // ─── Manual orders (UI-only with source filter) ────────────────────────
  // Manual placement keeps its purple ✋ chip — it's a distinct
  // lifecycle moment (operator click landed on the book). The manual
  // FILL collapses into the unified "filled" chip alongside automation
  // fills; the `source: manual` chip already distinguishes the source.
  manual_order_placed: {
    emoji: '✋',
    color: '#9c27b0',
    label: 'manual placed',
  },
  manual_order_executed: { emoji: '✅', color: '#4caf50', label: 'filled' },
  // Partial chunk of a manual order — same ✋-source chip, but the
  // row chip flips to 🟡 "partial" so the operator can scan partial
  // vs final fills at a glance. Shares the partial bucket with the
  // automation partials in the type-filter dropdown.
  manual_order_partial_fill: {
    emoji: '🟡',
    color: '#fdd835',
    label: 'partial',
  },
  // ─── UI + Slack (money / errors / TNA / digest) ────────────────────────
  execution_complete: {
    emoji: '🎯',
    color: '#2e7d32',
    label: 'cycle done',
  },
  // Reds for everything that needs operator eyes — error severity
  // shades from orange (warnings) → red (errors) → very dark red
  // (terminal / emergency).
  error: { emoji: '🚨', color: '#f44336' },
  counter_failed: { emoji: '🚨', color: '#d32f2f', label: 'counter failed' },
  // Counter maker resting unfilled too long — amber (operator-actionable
  // warning, not an error). The engine leaves the order resting.
  counter_stuck: { emoji: '⏳', color: '#ff9800', label: 'counter stuck' },
  // The all-clear for the line above. Green and quiet on purpose: it is the
  // END of a `counter_stuck` episode, so it has to be recognisable next to one
  // without competing with it for attention.
  counter_unstuck: { emoji: '✅', color: '#4caf50', label: 'counter unstuck' },
  // The stuck counter was escalated: the rule's (or opener group's)
  // counterStuckMarketHedgeSeconds elapsed, so the engine cancelled the
  // resting counter maker and hedged at market. Deep orange — money
  // crossed the spread, but by design, not by fault.
  counter_market_hedge: {
    emoji: '⏱️',
    color: '#ff5722',
    label: 'counter → market hedge',
  },
  counter_maker_walk_step: { emoji: '↗️', color: '#90caf9', label: 'walk step' },
  entry_counter_maker_walk_step: { emoji: '↗️', color: '#90caf9', label: 'walk step' },
  out_of_balance: {
    emoji: '💸',
    color: '#ff5722',
    label: 'out of balance',
  },
  rule_below_minimum: {
    emoji: '⚠️',
    color: '#ff9800',
    label: 'below min',
  },
  max_volume_reached: {
    emoji: '🛑',
    color: '#e67e22',
    label: 'max volume',
  },
  dust_remaining: { emoji: '🌑', color: '#795548', label: 'dust' },
  market_takeover: {
    emoji: '🌪️',
    color: '#e91e63',
    label: 'market takeover',
  },
  tna_negative: { emoji: '🔻', color: '#c62828', label: 'TNA negative' },
  net_contracts_digest: {
    emoji: '📊',
    color: '#3f51b5',
    label: 'digest',
  },
  // Amber (not red) — operator-initiated PANIC is a deliberate action,
  // not an engine error. Backend severity flipped to `warning` for the
  // same reason; sound layer skips the error chime.
  emergency_stop: {
    emoji: '🛑',
    color: '#ef6c00',
    label: 'panic stop',
  },
  emergency_resume: {
    emoji: '▶️',
    color: '#388e3c',
    label: 'panic resume',
  },
  // Recovery FILL collapses into the unified "filled" chip — a fill
  // is a fill regardless of which code path produced it. The expanded
  // panel still shows `Type: recovery_filled` for disambiguation.
  // `recovery_restored` (order put back on the book, no fill) stays
  // teal because it's a lifecycle event, not a fill.
  recovery_filled: { emoji: '✅', color: '#4caf50', label: 'filled' },
  recovery_restored: {
    emoji: '🔄',
    color: '#26a69a',
    label: 'recovery restored',
  },
  counter_skipped: {
    emoji: '⏭️',
    color: '#757575',
    label: 'counter skipped',
  },
  // ─── Slack only (ops monitoring) ───────────────────────────────────────
  // Greys — these don't surface on the page today (routing config
  // sends them to Slack only) but the entries exist so the type
  // map is exhaustive and a future routing flip needs zero UI work.
  platform_disconnect: {
    emoji: '🔌',
    color: '#424242',
    label: 'platform disconnect',
  },
  platform_reconnect: {
    emoji: '🔋',
    color: '#66bb6a',
    label: 'platform reconnect',
  },
  rule_started: { emoji: '▶️', color: '#26a69a', label: 'rule started' },
  rule_completed: { emoji: '🏁', color: '#7cb342', label: 'rule completed' },
  // Terminal-reason split. Genuine completion (full position unwound)
  // keeps 🏁. A pause (manual stop, error, removed-from-active) gets
  // ⏸️ in amber so the operator immediately sees "this rule did NOT
  // finish on its own". A cancel is a hard stop and gets the red ❌.
  rule_paused: { emoji: '⏸️', color: '#ff9800', label: 'rule paused' },
  rule_cancelled: { emoji: '❌', color: '#d32f2f', label: 'rule cancelled' },
  // A maker partial fill could not be taker-hedged under the hard $1
  // cap; the engine rested a passive counter maker instead of
  // abandoning the contracts.
  hedge_deferred: { emoji: '🪢', color: '#ff9800', label: 'hedge deferred' },
  // The opener sweep state machine changed operating mode (maker-first ↔
  // dual-post ride-the-sweep, or the fail-closed paused exit). Info-blue
  // for the normal edges; the paused/naked-remainder case rides the
  // event's `warning` severity for its loud styling, so the static map
  // entry stays the neutral mode-switch glyph.
  mode_change: { emoji: '⚡', color: '#42a5f5', label: 'sweep' },
  // Out-of-session hedge-check watchdog stopped a rule whose legs
  // drifted unhedged.
  hedge_unhedged_panic: { emoji: '🚨', color: '#d32f2f', label: 'hedge panic' },
  // Terminal per-session teardown summary (Rust engine). Orange/blue chip;
  // carries contractsBought / contractsHedged / nakedRemainder.
  session_summary: { emoji: '📋', color: '#0288d1', label: 'session summary' },
  // A superseded Polymarket maker's grace window expired holding an un-hedged
  // tail — naked money on one leg, red.
  naked_remainder: { emoji: '🩸', color: '#d32f2f', label: 'naked remainder' },
  // A post-teardown in-gap Polymarket fill was recovered — maker hedged on the
  // counter venue, or counter fill completed the hedge (book-only). Green-ish.
  grace_hedge: { emoji: '🧯', color: '#00897b', label: 'late fill recovered' },
  // Legs already held an unequal position at activation — the larger side will
  // be left orphaned. Amber lifecycle marker.
  starting_position_imbalance: {
    emoji: '⚖️',
    color: '#f9a825',
    label: 'starting imbalance',
  },
  // The maker yielded its resting position to the counter leg (the
  // counter filled first) — a lifecycle note, teal.
  maker_yielded_to_counter: {
    emoji: '🔄',
    color: '#26a69a',
    label: 'maker yielded',
  },
  // Orderbook freshness watchdog: a market's book went stale (cancel +
  // hold placement) → amber; came back fresh → green.
  book_stale: { emoji: '🧊', color: '#ff9800', label: 'book stale' },
  book_fresh: { emoji: '🌊', color: '#66bb6a', label: 'book fresh' },
  // Quiet-book fail-closed: a resting maker exceeded its no-re-evaluation
  // window and the fresh REST snapshot failed, so the engine cancelled the
  // maker rather than leave it resting blind. Deep red — needs operator eyes.
  maker_quiet_cancel: {
    emoji: '🔕',
    color: '#d32f2f',
    label: 'quiet cancel',
  },
  // The hard $1 cap trip — the cap fortress was breached. 🛡️ + deep
  // red so it's visually unmistakable; the single most important
  // operator-actionable opener signal.
  constraint_violated: {
    emoji: '🛡️',
    color: '#b71c1c',
    label: 'constraint violated',
  },
  // Position-balance faults — the engine bought/sold more than the
  // opposite leg, or the legs drifted apart. Reds because they need
  // operator eyes to reconcile.
  over_buy: { emoji: '⬆️', color: '#e53935', label: 'over-buy' },
  over_sell: { emoji: '⬇️', color: '#e53935', label: 'over-sell' },
  leg_imbalance: { emoji: '⚖️', color: '#ff5722', label: 'leg imbalance' },
  // Live engine task crashed — a terminal infra fault, darkest red.
  engine_live_task_panic: {
    emoji: '💥',
    color: '#b71c1c',
    label: 'engine panic',
  },
  // Rate-limit family — a place/amend/cancel was rejected (red) or is
  // waiting out the limiter before retrying (amber).
  order_rejected_rate_limited: {
    emoji: '🚦',
    color: '#d32f2f',
    label: 'rate limited',
  },
  order_rate_limit_waiting: {
    emoji: '⏳',
    color: '#ff9800',
    label: 'rate-limit wait',
  },
  // The venue reported the market CLOSED on a place/amend/cancel (Kalshi
  // closes in-play prop markets at kickoff; Polymarket closed/resolved). The
  // rule was paused. Amber — an expected lifecycle event, not a fault.
  market_closed: {
    emoji: '🔒',
    color: '#ff9800',
    label: 'market closed',
  },
  // Polymarket refused a maker place for crossing its unified book — usually
  // our own resting order on the sibling path. Grey/informational: nothing
  // rests, nothing is un-hedged, and the maker re-places on the next book
  // update. It used to arrive as a red `error`, which buried real faults.
  maker_cross_refused: {
    emoji: '🔀',
    color: '#78909c',
    label: 'self-cross refused',
  },
  // Informational drift alert — the engine's believed position drifted from
  // the authoritative exchange-API position beyond tolerance. Amber (a
  // reconciliation-gap heads-up, not an error/panic).
  belief_position_drift: {
    emoji: '⚖️',
    color: '#ff9800',
    label: 'belief drift',
  },
  // Fail-closed token-id invariant — the engine refused to trade a rule
  // whose pair lacks Polymarket yes/no token ids. Deep red because the
  // rule is refused/paused until the pair is fixed.
  pair_missing_token_ids: {
    emoji: '🧩',
    color: '#d32f2f',
    label: 'missing token ids',
  },
  // An executed Polymarket fill the engine could not attribute to a side
  // or order — the rule is paused and its orders cancelled rather than
  // dropping the fill. Deep red: money moved, position unhedged.
  unresolved_fill_naked_leg: {
    emoji: '🚨',
    color: '#d32f2f',
    label: 'unresolved fill',
  },
  // A venue-confirmed fill nothing could attribute to a slot. The engine booked
  // NOTHING and placed NOTHING rather than guess, so the believed position is
  // short by a real fill. Deep red like its siblings — money moved and only a
  // human closes it — but note the rule is NOT paused, so it keeps trading with
  // a short belief until someone acts on this card.
  unattributable_fill: {
    emoji: '🚨',
    color: '#d32f2f',
    label: 'unattributable fill',
  },
  // Report-only measurement: the recovery booked a fill into a slot our own
  // placement record contradicts. Amber, not red — nothing is unhedged by it,
  // and with `fillBookedOnceBySlot` OFF it is expected to fire. Counting these
  // over a night IS the rollout's baseline, and this feed is its only surface
  // (no Slack route), so it has to be legible rather than a grey fallback.
  duplicate_fill_booking_detected: {
    emoji: '👯',
    color: '#f39c12',
    label: 'double-booked fill',
  },
  // A trade the engine had already booked (and hedged) DIED at the venue —
  // the booked leg is gone while the irreversible hedge remains. Deep red:
  // money moved, position unhedged until the re-hedge / operator covers it.
  dead_trade_naked_leg: {
    emoji: '🚨',
    color: '#d32f2f',
    label: 'dead trade',
  },
  // An automation order filled on the venue with NO owning rule or live
  // engine — the account-wide watcher's naked-leg net (every scope-aware
  // engine demoted the fill as out-of-scope). Deep red: money moved,
  // nobody is hedging it.
  orphaned_automation_fill: {
    emoji: '🚨',
    color: '#d32f2f',
    label: 'orphaned fill',
  },
  // An order the engine tried to cancel is STILL LIVE at the venue. Same red as
  // the orphaned fill and for the same reason: unmanaged money that only a
  // human closes. Without this entry it renders as the grey 📌 fallback — which
  // is what it would have done the moment the backend split it off `error`.
  teardown_cancel_failed: {
    emoji: '🚨',
    color: '#d32f2f',
    label: 'order still live',
  },
  // The audit went BLIND on a venue — it could not look, which is not the same
  // as finding nothing. Amber, not red: no exposure is being asserted here.
  orphan_audit_venue_unreadable: {
    emoji: '🕶️',
    color: '#f59e0b',
    label: 'audit blind',
  },
  // Fail-closed staleness halt — rule state went unconfirmed past the
  // threshold and the engine HALTED all new placement. Deep red, the most
  // critical system-level halt the operator must see (and hear — the sound
  // layer alarms on it).
  rule_state_stale: {
    emoji: '🛑',
    color: '#d32f2f',
    label: 'halted',
  },
  // The all-clear after a staleness halt — placement resumed. Green, it's
  // a recovery, not a fault (silent in the sound layer).
  rule_state_recovered: {
    emoji: '✅',
    color: '#4caf50',
    label: 'recovered',
  },
  // The boot-time system-config all-clear: a pod that was reported as running
  // on guessed hedge config (mode / maxSum) has read the real singleton. It
  // needs its OWN type because this map colours by TYPE and never by severity —
  // the backend used to send it as `error` + `severity: 'info'`, which rendered
  // a red 🚨 for what is good news. Green, same as `rule_state_recovered`.
  system_config_recovered: {
    emoji: '✅',
    color: '#4caf50',
    label: 'config recovered',
  },
  // ─── Slack delivery health (2026-08-24) ────────────────────────────────
  // These exist because Slack went silent for ~8h and this feed was the only
  // place the operator could have found out. They must NOT fall through to the
  // neutral grey pin: an outage of the alerting channel that renders as
  // indistinguishable noise is the same failure again, one layer up.
  //
  // Red, `error` grade: while it is firing, every Slack card is being
  // destroyed on arrival, fills included.
  slack_delivery_failing: {
    emoji: '📵',
    color: '#d32f2f',
    label: 'slack down',
  },
  // Amber: delivery still works — this is the warning that buys time to act
  // before it stops.
  slack_budget_warning: {
    // NOT 📊 — that glyph is `net_contracts_digest`, and the emoji is what
    // carries a feed scanned at a glance. A routine report and "Slack is about
    // to stop delivering" must not share it.
    emoji: '🚧',
    color: '#ff9800',
    label: 'slack budget',
  },
  // Green all-clear. Its payload carries `messagesLost`, which is REAL data
  // loss and not a retry backlog: those cards exist only here, in this feed.
  //
  // NOT ✅ + #4caf50 — that PAIR is the FILL marker (`maker_filled`,
  // `counter_filled`, `crossing_executed`, `manual_order_executed`,
  // `recovery_filled`), and fills dominate the feed. Both halves have to move,
  // and the colour is the half that matters more: the row's left accent IS
  // this colour (`resolveNotificationColor`), so at a glance a card reporting
  // a thousand destroyed notifications would carry the fill bar and only the
  // glyph would separate them.
  //
  // Teal: reads as resolved without joining the fill family, and matches the
  // `warning` severity the backend actually stamps on this card — it is an
  // all-clear that still reports a loss.
  slack_delivery_recovered: {
    emoji: '📬',
    color: '#26a69a',
    label: 'slack recovered',
  },
  // The engine verified a book-cross against get_order and found the real
  // fill was smaller than assumed, so it withheld an over-hedge. Blue-grey
  // safety note — money was protected, no operator action needed (silent).
  book_cross_saved: {
    emoji: '🛡️',
    color: '#607d8b',
    label: 'over-hedge avoided',
  },
  // The hedge/counter leg over-bought vs the maker leg — real money
  // exposure on the counter side. Red, money-severity: needs operator
  // eyes to reconcile the excess.
  over_hedged: { emoji: '⚖️', color: '#f44336', label: 'over hedged' },
  // A hedge was reduced because excess contracts from a previous cycle
  // already covered it. Green informational — money was saved, no
  // operator action needed.
  counter_credit_applied: {
    emoji: '♻️',
    color: '#4caf50',
    label: 'credit applied',
  },
  // A counter whose create-ACK was lost got reconciled by COID and
  // re-adopted as the engine's own resting order — good news, green/info
  // (silent in the sound layer).
  counter_recovered: {
    emoji: '♻️',
    color: '#4caf50',
    label: 'counter recovered',
  },
  // A 0-fill cancelled counter was re-placed at a still-reachable price —
  // amber, an operator-visible warning that the hedge had to be re-armed.
  counter_rearmed: {
    emoji: '🔁',
    color: '#ff9800',
    label: 'counter re-armed',
  },
  // A counter could NOT be confirmed at the venue (429 storm) — the
  // operator must check the position by hand. Deep red, actionable (the
  // sound layer alarms on it).
  counter_unconfirmed: {
    emoji: '🚨',
    color: '#d32f2f',
    label: 'counter unconfirmed',
  },
  // A stuck counter-hedge escalation declined to cross the book above the
  // rule's threshold / max-sum cap and left the remainder resting as a GTC
  // maker instead of crossing. Amber — an operator-visible warning that the
  // hedge is capped and passively resting (same family as counter_stuck /
  // counter_rearmed), not an error: the engine deliberately did NOT breach
  // the cap.
  counter_capped_resting: {
    emoji: '🧢',
    color: '#ff9800',
    label: 'counter capped',
  },
  // ─── Counter/hedge price ladder ────────────────────────────────────────
  // One successful rung transition of the timed ladder. Informational, and
  // it shares the light blue of `counter_maker_walk_step` — the timed
  // reprice whose job the ladder is meant to take over — because it is the
  // same "automation is walking the resting counter" family. Nothing is owed
  // to the operator: it is routed `['mongo', 'realtime']` only, and the
  // sound hook leaves it silent (it fires up to twice per cycle on every
  // laddered leg).
  counter_ladder_step: { emoji: '🪜', color: '#90caf9', label: 'ladder step' },
  // Los dos del detector de rancias son UI-only en notifications-core. El
  // `detected` vive en `LIFECYCLE_NOISE_TYPES` (gris, oculto por default: es
  // contabilidad del motor con el cancel ya armado); el `cancel_resolved` se
  // queda en el feed principal porque es el que pide al operador, y va en
  // ambar para que se distinga de una fila de ciclo de vida.
  stale_resting_order_detected: {
    emoji: '🕰️',
    color: '#9e9e9e',
    label: 'stale resting',
  },
  stale_resting_order_cancel_resolved: {
    emoji: '🧹',
    color: '#ffb74d',
    label: 'stale cancel resolved',
  },
  // CRITICAL — the leg we are still exposed on appreciated past the rule's
  // `counterHedgeLadderAdverseTicks` while its hedge was still incomplete.
  // The engine keeps laddering the hedge; selling the appreciated leg is a
  // MANUAL operator action. Deep red + 🚨 to match the RED Slack attachment
  // this type also routes to (`buildCounterLadderLegAppreciatedMessage`),
  // and it is on the alarm whitelist.
  counter_ladder_leg_appreciated: {
    emoji: '🚨',
    color: '#d32f2f',
    label: 'leg appreciated',
  },
  // ─── Polymarket cancel path ────────────────────────────────────────────
  // The pending-cancel reaper re-issues a cancel for every fire-and-forget
  // Poly cancel the venue still shows resting. Its ladder escalates amber →
  // deep red, so the colours track how much operator action is owed.
  //
  // Still resting after N re-cancels (or unreadable). The reaper keeps
  // retrying, so this is amber: a duplicate/orphaned live maker is POSSIBLE,
  // not confirmed.
  poly_cancel_unconfirmed: {
    emoji: '⏳',
    color: '#ff9800',
    label: 'poly cancel unconfirmed',
  },
  // The reaper STOPPED re-issuing. The entry is kept, so the leg's create
  // gate stays closed until someone cancels at the venue by hand — the one
  // row here that always needs a human. Deep red.
  poly_cancel_abandoned: {
    emoji: '🛑',
    color: '#d32f2f',
    label: 'poly cancel abandoned',
  },
  // Systemic, not per-order: the registry blew past its bound and NOTHING is
  // evicted, so every entry is holding its leg's create gate shut. Deep red.
  poly_pending_cancel_registry_overflow: {
    emoji: '🚨',
    color: '#d32f2f',
    label: 'poly cancel registry overflow',
  },
  // A cancel+replace whose cancel could not be confirmed off-book: the
  // replacement was withheld so two live makers can never rest on the same
  // leg. Amber, deliberately — the engine did the SAFE thing, it is not a
  // fault. But the leg is left PARKED, which is why it stays visible in the
  // feed (never folded into the hidden-by-default lifecycle noise) and why
  // the pause glyph, not a generic warning triangle.
  poly_replace_withheld: {
    emoji: '⏸️',
    color: '#ff9800',
    label: 'poly replace withheld',
  },
  // Forfait protection armed — the pair stopped being an arbitrage and the
  // engine closed the paths that would keep paying above the Poly cap. Deep
  // red: money was already at risk when it fired. Single glyph (a two-codepoint
  // emoji renders double-width and breaks the chip alignment).
  forfait_protection_triggered: {
    emoji: '🎾',
    color: '#d32f2f',
    label: 'forfait protection',
  },
  // The provisional hold and its all-clear. Same 🎾 as the armed card — it is
  // the same brake, one verdict earlier — but amber, because nothing is settled
  // yet and there is nothing for the operator to do; and green on the release,
  // which is the feature working rather than an incident.
  forfait_hold_started: {
    emoji: '🎾',
    color: '#ed6c02',
    label: 'forfait hold',
  },
  forfait_hold_released: {
    emoji: '🎾',
    color: '#2e7d32',
    label: 'forfait hold',
  },
  // Amber, NOT the release's green. The hold was let go without anybody reading
  // the book, so the glanceable signal must not say "resolved, nothing to see"
  // while the text says a verdict was never taken.
  forfait_hold_dropped: {
    emoji: '🎾',
    color: '#ed6c02',
    label: 'forfait hold',
  },
  // ─── Venue maintenance (scheduled Kalshi / Polymarket windows) ─────────
  // The T-10 heads-up, and the only card in this family that arms nothing. A
  // clock rather than the family's 🛑: what it hands the operator is TIME —
  // seven minutes to decide whether this window gets cut — and reading it as
  // "trading stopped" is the one mistake that makes the gap useless. Amber like
  // its neighbours because the countdown is running either way: armed, the
  // platform stops itself; off, both engines trade into the outage.
  // 🗓️, NOT ⏳: the hourglass is `counter_stuck`'s — the row that means there is
  // naked money on the book RIGHT NOW, and the only one with its own sound clip
  // — and it is shared with `order_rate_limit_waiting` and
  // `poly_cancel_unconfirmed` besides. A calendar is what this is: a date the
  // venue put on the board.
  venue_maintenance_scheduled: {
    emoji: '🗓️',
    color: '#ff9800',
    label: 'maintenance in 10 min',
  },
  // The halt shares the panic 🛑 on purpose: whatever the cause, trading just
  // stopped account-wide and that has to read the same way at a glance. Amber,
  // not red — this is the system protecting itself on schedule, not a fault.
  venue_maintenance_halt: {
    emoji: '🛑',
    color: '#ef6c00',
    label: 'venue maintenance',
  },
  // The panic's receipt: cancelled everything, both engines halted, and both
  // claims re-read rather than assumed. A fire extinguisher, not a green tick —
  // the platform is stopped account-wide, which is never an all-clear. Same
  // amber family as the halt it closes; the closing card belongs to the halt-lift.
  // Label deliberately NEUTRAL: this card is emitted with `verified: false` too,
  // and the chip is the glanceable half of the row. "panic verified" over a
  // panic that explicitly could not be verified is the worst possible 4am read.
  // The verdict lives in the summary, which states it either way.
  venue_maintenance_panic: {
    emoji: '🧯',
    color: '#ef6c00',
    label: 'maintenance panic',
  },
  // The window closed: this card is an ASK, not an all-clear. Amber and a hand,
  // because nothing moves until a human acts. The LABEL stays neutral on
  // purpose — this map is per TYPE and cannot see `enforced`, and the same type
  // carries the observe-only payload where nothing was ever halted. What
  // happened is in the summary, which does branch.
  venue_maintenance_window_over: {
    emoji: '🙋',
    color: '#ff9800',
    label: 'maintenance window over',
  },
  // The closing half of the pair. It exists so the operator learns the file is
  // CLOSED: an alert with no resolution event is what left 72 open questions in
  // the feed on 2026-08-13. NOT green: lifting the halt is not an all-clear —
  // it clears a gate, and the same type also carries the "left ARMED" payload
  // where the key was not cleared at all. Slate rather than the amber of the
  // open questions beside it, so it still reads as a resolution.
  venue_maintenance_resumed: {
    emoji: '🔓',
    color: '#78909c',
    label: 'maintenance halt-lift',
  },
  // Nothing is halted (the watcher fails open) — but the pre-emptive protection
  // is off and the operator is flying blind into the next window.
  venue_maintenance_probe_blind: {
    emoji: '🙈',
    color: '#ff9800',
    label: 'venue status blind',
  },
  // ─── Sniper (live-event directional sweep) ─────────────────────────────
  // The FIRE is the ⚡ — the sweep going out the instant the incident lands. Every
  // other sniper op carries its own symbol so a fill, an exit and a retraction are
  // never read as "another one of those" at a glance.
  sniper_fired: { emoji: '⚡', color: '#4caf50', label: 'sniper fired' },
  // A period-close auto-NO tail swept the No/Under side — a fire too, on the
  // opposite side, so the same bolt with its own label.
  sniper_auto_no_fired: {
    emoji: '⚡',
    color: '#4caf50',
    label: 'sniper auto-NO fired',
  },
  // A VAR-defense SELL exit (Phase-1 break-even / Phase-2 dump-to-floor) — we sold
  // out of a goal under VAR. Amber (defensive), with the shield of an exit taken to
  // protect the position rather than to take a profit.
  sniper_var_reversal: {
    emoji: '🛡️',
    color: '#ff9800',
    label: 'sniper VAR sell',
  },
  // LSports RETRACTED a goal we hold a position from: the result is in DOUBT and an
  // exit is running. Deep red — this is the one sniper row that always deserves the
  // operator's eyes, and it carries the retraction's own confidence.
  sniper_goal_cancelled: {
    emoji: '❌',
    color: '#d32f2f',
    label: 'goal cancelled — result in doubt',
  },
  // A VAR all-clear REBUY — the goal was confirmed and we bought the position back.
  // A BUY, distinct from the sell above (blue 🔁).
  sniper_var_rebuy: {
    emoji: '🔁',
    color: '#2196f3',
    label: 'sniper VAR rebuy',
  },
  // Fail-closed: NO order placed (no book / config ambiguity). Deep red.
  sniper_fire_blocked: {
    emoji: '🚫',
    color: '#d32f2f',
    label: 'sniper blocked',
  },
  // The confidence ladder's resting exit. Placement/amend are informational
  // (purple, the ladder's colour); the FILL is the money event, so it gets the
  // same green as any other fill.
  sniper_ladder_sell_placed: {
    emoji: '🪜',
    color: '#8e5bf5',
    label: 'ladder sell placed',
  },
  // "amended" is wire-speak and leaves open whether anything traded; the cover
  // was repriced or resized and is STILL working, which is what the label says.
  sniper_ladder_sell_amended: {
    emoji: '🪜',
    color: '#8e5bf5',
    label: 'ladder sell repriced',
  },
  // The cover actually traded — the money event, and the one the operator was
  // missing entirely while the engine discarded its own cover fills. Distinct from
  // the BUY fill below so the two directions never blur together.
  sniper_ladder_sell_filled: {
    emoji: '💵',
    color: '#4caf50',
    label: 'ladder sell filled',
  },
  // Every booked BUY fill delta on the sniper's entry order. ONE type for both
  // partial and complete — the row derives which from `filled` vs
  // `sizeRequested`, so the chip colour stays the fill green either way.
  sniper_buy_filled: { emoji: '💰', color: '#4caf50', label: 'buy filled' },
};

/**
 * Direction axis: which side of the arbitrage lifecycle a notification
 * is about. Surfaced as a chip on each row so the operator can scan
 * "open" vs "close" without disambiguating from the chip label (which
 * is intentionally identical across directions — a `maker_placed` is
 * the same kind of event whether the engine is opening or closing a
 * position; only `evt.direction` differs).
 *
 *   - `entry`  — opener service (BUY direction, OPENING a position)
 *   - `unwind` — unwinder service (SELL direction, CLOSING a position)
 *   - `system` — direction-less ops events (platform disconnect, digest,
 *                tna_negative, out_of_balance, manual orders, emergency,
 *                rule_started/completed without an entry/unwind context)
 *
 * The backend now stamps `direction` explicitly on every event, so the
 * primary path is `evt.direction`. The legacy type-prefix / system-set
 * inference is kept ONLY as a fallback for any not-yet-migrated
 * in-flight doc that lands without the field — once the Mongo
 * migration has renamed the historical docs this branch is dead code.
 */
const SYSTEM_TYPES: ReadonlySet<string> = new Set([
  // Slack delivery health — a property of the transport, not of a side. Listed
  // even though the backend stamps `direction: 'system'` on all three: an
  // emitter that forgets to stamp fails NOWHERE, and the fallback below
  // defaults to `unwind`, which would paint a platform-wide outage with an
  // "Unwind" chip and hide it from the `Direction = System` filter — the exact
  // filter an operator reaches for when hunting a Slack outage.
  'slack_budget_warning',
  'slack_delivery_failing',
  'slack_delivery_recovered',
  'platform_disconnect',
  'platform_reconnect',
  'net_contracts_digest',
  'tna_negative',
  'out_of_balance',
  'emergency_stop',
  'emergency_resume',
  'manual_order_placed',
  'manual_order_executed',
  'manual_order_partial_fill',
  'belief_position_drift',
  'venue_maintenance_scheduled',
  'venue_maintenance_halt',
  'venue_maintenance_panic',
  'venue_maintenance_window_over',
  'venue_maintenance_resumed',
  'venue_maintenance_probe_blind',
]);

/**
 * Resolve the direction of a notification. Reads the explicit
 * `direction` field first; falls back to the legacy type-name
 * heuristic when passed a bare type string or an event missing the
 * field.
 */
export function getNotificationDirection(
  evtOrType: NotificationEvent | NotificationEventType | string,
): NotificationDirection {
  if (typeof evtOrType === 'object' && evtOrType !== null) {
    if (evtOrType.direction) return evtOrType.direction;
    return directionFromType(evtOrType.type);
  }
  return directionFromType(evtOrType);
}

function directionFromType(
  type: NotificationEventType | string,
): NotificationDirection {
  if (typeof type === 'string' && type.startsWith('entry_')) return 'entry';
  if (SYSTEM_TYPES.has(type)) return 'system';
  return 'unwind';
}

/**
 * Display config for the direction chip itself. Kept beside the per-
 * type display so a single import in the page component covers both.
 */
export const DIRECTION_DISPLAY: Record<
  NotificationDirection,
  { color: string; label: string }
> = {
  // Soft purple — direction is its own dimension, distinct from
  // severity (red/orange/info) and source (chip outline). Picking a
  // hue not used elsewhere on the row keeps the visual hierarchy
  // unambiguous.
  entry: { color: '#7e57c2', label: 'Entry' },
  unwind: { color: '#5c6bc0', label: 'Unwind' },
  system: { color: '#78909c', label: 'System' },
};

const FALLBACK: NotificationDisplayConfig = {
  emoji: '📌',
  color: '#9e9e9e',
};

/**
 * Returns the display config for a given event type, falling back to
 * a neutral grey pin if the type is unknown — defensive against
 * backend rolling out a new event before the frontend is updated.
 * An `entry_`-prefixed raw type (the opener engine's pre-dispatch
 * names, e.g. `entry_counter_market_hedge`) resolves to its base
 * entry — the direction chip already carries the entry/unwind axis.
 */
export function getNotificationDisplay(
  type: NotificationEventType | string,
): NotificationDisplayConfig {
  const map = NOTIFICATION_DISPLAY as Record<string, NotificationDisplayConfig>;
  return (
    map[type] ??
    (typeof type === 'string' && type.startsWith('entry_')
      ? map[type.slice('entry_'.length)]
      : undefined) ??
    FALLBACK
  );
}
